/**
 * Renders an AI text field through the three structure marks
 * (`text-marks.ts`): paragraphs, bullets and bold-labelled fields, with
 * inline bold. Used by the portal's AI analysis panel and by the
 * `AiReadoutCard` block, so the asterisks of the action insight
 * (`**A quién:** …`) read as labels on screen the way they do in the
 * scheduled email.
 *
 * Deliberately unstyled beyond structure: colour and size come from the
 * host through `className`, exactly as the raw `<p>` did before PR1.
 */
import { parseMarks, type MarkBlock, type MarkSpan } from "./text-marks";

function Spans({ spans }: { spans: MarkSpan[] }): JSX.Element {
  return (
    <>
      {spans.map((span, i) =>
        span.bold ? (
          <strong key={i} className="font-semibold">
            {span.text}
          </strong>
        ) : (
          <span key={i}>{span.text}</span>
        ),
      )}
    </>
  );
}

function Block({ block }: { block: MarkBlock }): JSX.Element {
  if (block.kind === "bullets") {
    return (
      <ul className="list-disc space-y-1 pl-5">
        {block.items.map((item, i) => (
          <li key={i}>
            <Spans spans={item} />
          </li>
        ))}
      </ul>
    );
  }
  if (block.kind === "field") {
    return (
      <p>
        <strong className="font-semibold">{block.label}:</strong>
        {block.value.length > 0 && (
          <>
            {" "}
            <Spans spans={block.value} />
          </>
        )}
      </p>
    );
  }
  return (
    <p>
      {block.lines.map((line, i) => (
        <span key={i}>
          {i > 0 && <br />}
          <Spans spans={line} />
        </span>
      ))}
    </p>
  );
}

export interface MarkedTextProps {
  text: string;
  /** Applied to the wrapping element; the blocks inherit from it. */
  className?: string;
}

export function MarkedText({ text, className }: MarkedTextProps): JSX.Element {
  const blocks = parseMarks(text);
  return (
    <div className={className ? `space-y-2 ${className}` : "space-y-2"}>
      {blocks.map((block, i) => (
        <Block key={i} block={block} />
      ))}
    </div>
  );
}
